/**
 * Build-time page data snapshot loader.
 *
 * During production builds, `scripts/generate-page-data.js` writes per-route
 * JSON under public/page-data/. This module fetches those snapshots on page
 * load so the app renders real content instantly (critical for prerender
 * capture) and falls through to the live API as revalidation.
 *
 * Snapshots match the api-salary / api-occupation-enrichment response shapes.
 */

// Cache fetched snapshots to avoid duplicate network requests within a session.
const snapshotCache = new Map<string, unknown>();

async function fetchSnapshot<T>(url: string): Promise<T | null> {
  const cached = snapshotCache.get(url);
  if (cached !== undefined) return cached as T | null;

  try {
    const res = await fetch(url);
    if (!res.ok) {
      snapshotCache.set(url, null);
      return null;
    }
    const data = await res.json();
    snapshotCache.set(url, data);
    return data as T;
  } catch {
    snapshotCache.set(url, null);
    return null;
  }
}

/**
 * Fetch the build-time salary snapshot for a given occupation slug + location.
 * Returns null when no snapshot exists (dev mode, non-curated routes, etc.).
 */
export async function getSalarySnapshot(
  occupationSlug: string,
  locationSlug: string
): Promise<Record<string, unknown> | null> {
  return fetchSnapshot(`/page-data/salary/${occupationSlug}/${locationSlug}.json`);
}

/**
 * Fetch the build-time enrichment snapshot for a given SOC code.
 */
export async function getEnrichmentSnapshot(
  soc: string
): Promise<Record<string, unknown> | null> {
  return fetchSnapshot(`/page-data/enrichment/${soc}.json`);
}

export interface SocMapEntry {
  soc: string;
  title: string;
}

let socMapPromise: Promise<Record<string, SocMapEntry> | null> | null = null;

/**
 * Fetch the slug-to-SOC mapping generated at build time.
 * Singleton — the map is fetched once and cached for the session.
 */
export function getSocMap(): Promise<Record<string, SocMapEntry> | null> {
  if (!socMapPromise) {
    socMapPromise = fetchSnapshot<Record<string, SocMapEntry>>('/page-data/soc-map.json');
  }
  return socMapPromise;
}
